// Components==============
import Button from 'components/UI/Button/Button';
import ProgressGraph from 'components/UI/ProgressGraph/ProgressGraph';
import Slider from 'components/UI/Slider/Slider';
import { motion } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import EmojiSelector from 'pages/Growth/Reflect/components/ReflectionItem/components/EmojiSelector/EmojiSelector';
import { ReflectContext } from 'pages/Growth/Reflect/Reflect';
import React, { useContext, useEffect, useState } from 'react';
import { framerFade } from 'utilities/FramerAnimations';
import { ReflectionItemContext } from '../../ReflectionItem';
import ReflectionTextarea from '../../ReflectionTextarea/ReflectionTextarea';
import './PeerEndPointReflection.module.scss';
// =========================

export default function PeerEndPointReflection() {
  const { currentStudent, project } = useContext(ReflectContext);

  const { save, saveState, reflection, reflectingOn } = useContext(
    ReflectionItemContext
  );
  const { endPointActivities } = useContext(ReflectContext);

  const [value1, setValue1] = useState(reflection?.note?.[0]?.answer || '');
  const [value2, setValue2] = useState(reflection?.note?.[1]?.answer || '');

  const userId = useRedux((state) => state.authentication?.user?.id);

  useEffect(() => setValue1(reflection?.note?.[0]?.answer || ''), [
    reflection?.note?.[0]?.answer || '',
  ]);

  useEffect(() => setValue2(reflection?.note?.[1]?.answer || ''), [
    reflection?.note?.[1]?.answer || '',
  ]);

  const peerEndNotes: ReflectionNoteEntity[] = [
    {
      question: {
        nl: 'Resultaat',
        en: 'Result',
      },
      description: {
        nl: 'Wat vind je van het eindresultaat van deze student?',
        en: 'What do you think of the end result of this student?',
      },
      placeholder: {
        nl: 'Feedback resultaat',
        en: 'Feedback result',
      },
      answer: value1,
    },
    {
      question: {
        nl: 'Verloop proces',
        en: 'Process progress',
      },
      description: {
        nl: 'Hoe vind je dat de student dit proces doorlopen heeft?',
        en: 'How do you think this student has gone through this process?',
      },
      placeholder: {
        nl: 'Feedback verloop proces',
        en: 'Feedback process progress',
      },
      answer: value2,
      created_by: userId || 0,
    },
  ];

  const graphData = useRedux((state) => {
    const reflections = state.reflection?.entities.allIds.map(
      (id) => state.reflection?.entities.byId[id]
    );

    return endPointActivities
      .map(
        (e) =>
          reflections?.find(
            (r) =>
              r?.project_process_id === e?.id &&
              r?.created_by === currentStudent?.userId
          ) || 'filler'
      )
      .filter((e) => e);
  });

  const translate = useTranslate();

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setValue1((prev) => prev.trim());
    setValue2((prev) => prev.trim());
    save({
      ...saveState,
      note: peerEndNotes,
      finished: true,
      reflection_on_student: currentStudent?.userId,
    });
  }

  return (
    <motion.div styleName="container" {...framerFade}>
      {!!project && (
        <div>
          <ProgressGraph
            data={graphData}
            project={project}
            topLeft={
              <div style={{ display: 'flex', gap: '0.25em' }}>
                <strong>{translate('reflection.terms.progress')}:</strong>
                <p>{translate('reflection.experience-vs-result')}</p>
              </div>
            }
          />
        </div>
      )}
      {!!(
        !!reflectingOn?.emotions.length ||
        !!reflectingOn?.result ||
        !!reflectingOn?.note?.length
      ) && (
        <div styleName="wrapper">
          {!!reflectingOn?.emotions.length && (
            <div>
              <h3>{translate('reflection.terms.experience')}</h3>
              <EmojiSelector staticContent={reflectingOn.emotions} />
            </div>
          )}
          {!!reflectingOn?.result && (
            <div>
              <h3>{translate('general.terms.result')}</h3>
              <span>{translate('reflection.reflect-on-activity')}</span>
              {!!reflectingOn?.result && (
                <Slider value={reflectingOn?.result} />
              )}
            </div>
          )}
          {!!reflectingOn?.note?.length &&
            reflectingOn.note.map((r, i) => (
              <ReflectionTextarea item={r} key={i} />
            ))}
        </div>
      )}
      <form styleName="wrapper" onSubmit={handleSubmit}>
        {peerEndNotes.map((note, i) => (
          <ReflectionTextarea
            key={i}
            item={note}
            value={i === 0 ? value1 : value2}
            setValue={i === 0 ? setValue1 : setValue2}
          />
        ))}
        <Button
          color="grey"
          inactive={
            (!value1 || value1 === reflection?.note?.[0]?.answer) &&
            (!value2 || value2 === reflection?.note?.[1]?.answer)
          }
        >
          {translate('general.terms.judge')}
        </Button>
      </form>
    </motion.div>
  );
}
